import { supabase } from "@/integrations/supabase/client";
import { typedSupabase } from "@/integrations/supabase/database-extensions";
import type { SessionTagSource } from "@/components/analysis/sessionTags";
import type { AnalysisFlow } from "@/components/analysis/AnalysisDashboard";
import type {
  RecordSessionStateRequest,
  RecordSessionStateResponse,
  SessionStateEntry,
  SessionSummary,
  StepTransitionTime,
  FunnelStep,
  MarketingSourceData,
  JourneyPath,
  AnalysisFilter,
} from "@/types/sessionState";
import type { BookingStep, AnalysisStep } from "@/types/booking";
import type { DateFilterType } from "@/components/analysis/DateRangeFilter";

// Filtered views that centralize all session exclusion logic (IP, UA, non-production, test traffic)
const FILTERED_SUMMARIES = "filtered_session_summaries";
const FILTERED_ENTRIES = "filtered_session_state_entries";
// Typed as a plain string: supabase-js's select parser gives up on the JSON
// paths (TS2589), and the rows are cast to SessionTagSource below.
const TAG_COLUMNS: string =
  "session_id, created_at, flow, url_params, market:state->>market, packageType:state->>selectedPackageType, attachedScanId:state->membershipFirstBasket->>attachedScanId";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Helper to get the date column based on filter type
function getDateColumn(dateFilterType: DateFilterType): string {
  return dateFilterType === "first_contact" ? "started_at" : "updated_at";
}

/**
 * Service for managing session state tracking
 */
export const sessionStateService = {
  /**
   * Record a state transition via edge function
   * Called on each step change to persist state to server
   */
  async recordStateTransition(
    params: RecordSessionStateRequest,
  ): Promise<RecordSessionStateResponse> {
    try {
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/record-session-state`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
          },
          body: JSON.stringify(params),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        console.error("[SessionState] Failed to record transition:", error);
        return { success: false, message: error.message || "Request failed" };
      }

      const data: RecordSessionStateResponse = await response.json();
      console.log("[SessionState] Transition recorded:", data.entry_id);
      return data;
    } catch (error) {
      console.error("[SessionState] Error recording transition:", error);
      return { success: false, message: "Network error" };
    }
  },

  /**
   * Get the latest state entry for a session
   * Used to restore state on page load and tab sync
   * Uses Postgres RPC (SECURITY DEFINER) to bypass RLS
   */
  async getLatestStateEntry(
    sessionId: string,
  ): Promise<SessionStateEntry | null> {
    try {
      const { data, error } = await typedSupabase.rpc(
        "get_latest_session_state",
        { p_session_id: sessionId },
      );

      if (error) {
        console.error("[SessionState] Failed to fetch latest entry:", error);
        return null;
      }

      if (!data) {
        return null;
      }

      return data as unknown as SessionStateEntry;
    } catch (error) {
      console.error("[SessionState] Error fetching latest entry:", error);
      return null;
    }
  },

  /**
   * Get all state entries for a session (for analysis)
   */
  async getSessionEntries(sessionId: string): Promise<SessionStateEntry[]> {
    try {
      const { data, error } = await typedSupabase
        .from("session_state_entries")
        .select("*")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("[SessionState] Error fetching session entries:", error);
        return [];
      }

      return (data as unknown as SessionStateEntry[]) || [];
    } catch (error) {
      console.error("[SessionState] Error fetching session entries:", error);
      return [];
    }
  },

  /**
   * The latest entry's market and MRI/blood fields for each session, for the
   * session list's pills. Only those fields of the state are read; the step
   * is left to the caller (the session's max step reached).
   */
  async getSessionTagSources(
    sessionIds: string[],
  ): Promise<Map<string, Omit<SessionTagSource, "step">>> {
    if (sessionIds.length === 0) return new Map();
    const { data, error } = await typedSupabase
      .from("session_state_entries")
      .select(TAG_COLUMNS)
      .in("session_id", sessionIds)
      .order("created_at", { ascending: false });
    if (error) throw error;
    const rows = (data ?? []) as unknown as (Omit<SessionTagSource, "step"> & {
      session_id: string;
    })[];
    // Newest first: the first row seen for a session is its latest.
    const latest = new Map<string, Omit<SessionTagSource, "step">>();
    rows.forEach((row) => {
      if (!latest.has(row.session_id)) latest.set(row.session_id, row);
    });
    return latest;
  },

  /**
   * Get session summary
   */
  async getSessionSummary(sessionId: string): Promise<SessionSummary | null> {
    try {
      const { data, error } = await typedSupabase
        .from("session_summaries")
        .select("*")
        .eq("session_id", sessionId)
        .single();

      if (error) {
        if (error.code === "PGRST116") return null;
        console.error("[SessionState] Error fetching session summary:", error);
        return null;
      }

      return data as unknown as SessionSummary;
    } catch (error) {
      console.error("[SessionState] Error fetching session summary:", error);
      return null;
    }
  },

  // =========================================================================
  // ANALYSIS QUERIES (require authenticated user)
  // =========================================================================

  /**
   * Check if current user is authorized for analysis
   */
  async checkAnalysisAccess(): Promise<boolean> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.email) return false;

      const { data, error } = await typedSupabase
        .from("analysis_users")
        .select("email")
        .eq("email", user.email)
        .single();

      if (error || !data) return false;
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Get session summaries with filters (for analysis dashboard)
   */
  async getSessionSummaries(options: {
    limit?: number;
    offset?: number;
    maxStepFilter?: AnalysisStep | null;
    dateFrom?: Date | null;
    dateTo?: Date | null;
    completedOnly?: boolean;
  }): Promise<{ data: SessionSummary[]; count: number }> {
    try {
      let query = typedSupabase
        .from(FILTERED_SUMMARIES)
        .select("*", { count: "exact" });

      if (options.maxStepFilter) {
        query = query.eq("max_step_reached", options.maxStepFilter);
      }

      if (options.dateFrom) {
        query = query.gte("started_at", options.dateFrom.toISOString());
      }

      if (options.dateTo) {
        query = query.lte("started_at", options.dateTo.toISOString());
      }

      if (options.completedOnly) {
        query = query.eq("is_completed", true);
      }

      query = query
        .order("started_at", { ascending: false })
        .range(
          options.offset || 0,
          (options.offset || 0) + (options.limit || 50) - 1,
        );

      const { data, error, count } = await query;

      if (error) {
        console.error("[SessionState] Error fetching summaries:", error);
        return { data: [], count: 0 };
      }

      return {
        data: (data as unknown as SessionSummary[]) || [],
        count: count || 0,
      };
    } catch (error) {
      console.error("[SessionState] Error fetching summaries:", error);
      return { data: [], count: 0 };
    }
  },

  /**
   * Get funnel data
   */
  async getFunnelData(daysBack: number = 30): Promise<FunnelStep[]> {
    try {
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - daysBack);

      const { data, error } = await typedSupabase.rpc("get_funnel_data", {
        date_from: dateFrom.toISOString(),
      });

      if (error) {
        console.error("[SessionState] Error fetching funnel data:", error);
        // Fallback: calculate from summaries
        return this.calculateFunnelFromSummaries(daysBack);
      }

      return data as unknown as FunnelStep[];
    } catch (error) {
      console.error("[SessionState] Error fetching funnel data:", error);
      return this.calculateFunnelFromSummaries(daysBack);
    }
  },

  /**
   * Fallback funnel calculation from session_summaries
   */
  async calculateFunnelFromSummaries(daysBack: number): Promise<FunnelStep[]> {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - daysBack);

    const stepOrder: BookingStep[] = [
      "product",
      "addons",
      "clinic",
      "date",
      "details",
      "confirmation",
    ];

    const stepOrderMap: Record<string, number> = {
      product: 1,
      cadence: 2, // membership flow -- sibling of addons
      addons: 2,
      clinic: 3,
      date: 4,
      time: 4, // Legacy - treated same as date for ordering
      details: 5,
      confirmation: 6,
      // Membership-first flow steps
      entry: 1,
      plan: 2,
      payment: 3,
      booked: 7,
    };

    const { data, error } = await typedSupabase
      .from(FILTERED_SUMMARIES)
      .select("max_step_reached")
      .gte("started_at", dateFrom.toISOString());

    if (error || !data) return [];

    const funnelCounts: Record<string, number> = {
      product: 0,
      cadence: 0,
      addons: 0,
      clinic: 0,
      date: 0,
      time: 0,
      details: 0,
      confirmation: 0,
      // Membership-first flow steps
      entry: 0,
      plan: 0,
      payment: 0,
      booked: 0,
    };

    for (const session of data as Array<{ max_step_reached: string | null }>) {
      const maxStep = session.max_step_reached as AnalysisStep;
      if (!maxStep) continue;

      const maxOrder = stepOrderMap[maxStep];
      for (const step of stepOrder) {
        if (stepOrderMap[step] <= maxOrder) {
          funnelCounts[step]++;
        }
      }
    }

    return stepOrder.map((step, index) => ({
      step,
      sessions_reached: funnelCounts[step],
      drop_off_rate:
        index > 0 && funnelCounts[stepOrder[index - 1]] > 0
          ? (1 - funnelCounts[step] / funnelCounts[stepOrder[index - 1]]) * 100
          : 0,
    }));
  },

  /**
   * Get average time between steps
   */
  async getStepTransitionTimes(
    daysBack: number = 30,
  ): Promise<StepTransitionTime[]> {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - daysBack);

    try {
      // This would ideally be a database function for performance
      // For now, calculate client-side from entries
      const { data: entries, error } = await typedSupabase
        .from(FILTERED_ENTRIES)
        .select("session_id, step_name, created_at")
        .gte("created_at", dateFrom.toISOString())
        .order("session_id")
        .order("created_at");

      if (error || !entries) {
        console.error(
          "[SessionState] Error fetching entries for timing:",
          error,
        );
        return [];
      }

      // Group by session
      const sessionEntries = new Map<
        string,
        Array<{ step_name: string; created_at: string }>
      >();
      for (const entry of entries as Array<{
        session_id: string;
        step_name: string;
        created_at: string;
      }>) {
        const session = sessionEntries.get(entry.session_id) || [];
        session.push({
          step_name: entry.step_name,
          created_at: entry.created_at,
        });
        sessionEntries.set(entry.session_id, session);
      }

      // Calculate transitions
      const transitionTimes = new Map<string, number[]>();

      for (const [, sessionData] of sessionEntries) {
        for (let i = 0; i < sessionData.length - 1; i++) {
          const from = sessionData[i];
          const to = sessionData[i + 1];

          // Skip same-step transitions (page refreshes/reactivations)
          if (from.step_name === to.step_name) continue;

          const key = `${from.step_name}→${to.step_name}`;
          const duration =
            (new Date(to.created_at).getTime() -
              new Date(from.created_at).getTime()) /
            1000;

          if (duration > 0 && duration < 3600) {
            // Ignore > 1 hour (likely session break)
            const times = transitionTimes.get(key) || [];
            times.push(duration);
            transitionTimes.set(key, times);
          }
        }
      }

      // Calculate averages
      const results: StepTransitionTime[] = [];
      for (const [key, times] of transitionTimes) {
        const [from, to] = key.split("→") as [AnalysisStep, AnalysisStep];
        const sorted = [...times].sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        const avg = times.reduce((a, b) => a + b, 0) / times.length;

        results.push({
          from_step: from,
          to_step: to,
          avg_seconds: Math.round(avg * 10) / 10,
          median_seconds: Math.round(median * 10) / 10,
          transitions: times.length,
        });
      }

      return results.sort((a, b) => {
        const stepOrder = [
          "product",
          "addons",
          "clinic",
          "date",
          "details",
          "confirmation",
        ];
        return stepOrder.indexOf(a.from_step) - stepOrder.indexOf(b.from_step);
      });
    } catch (error) {
      console.error(
        "[SessionState] Error calculating transition times:",
        error,
      );
      return [];
    }
  },

  /**
   * Get marketing source breakdown
   */
  async getMarketingSourceData(
    daysBack: number = 30,
  ): Promise<MarketingSourceData[]> {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - daysBack);

    try {
      const { data, error } = await typedSupabase
        .from(FILTERED_SUMMARIES)
        .select("first_url_params, is_completed")
        .gte("started_at", dateFrom.toISOString());

      if (error || !data) {
        console.error("[SessionState] Error fetching marketing data:", error);
        return [];
      }

      // Group by source
      const sourceGroups = new Map<
        string,
        {
          sessions: number;
          conversions: number;
          medium: string | null;
          campaign: string | null;
        }
      >();

      for (const session of data as Array<{
        first_url_params: Record<string, string> | null;
        is_completed: boolean;
      }>) {
        const params = session.first_url_params;

        let source = "direct";
        let medium: string | null = null;
        let campaign: string | null = null;

        if (params) {
          if (params.utm_source) {
            source = params.utm_source;
            medium = params.utm_medium || null;
            campaign = params.utm_campaign || null;
          } else if (params.gclid) {
            source = "google_ads";
          } else if (params.fbclid) {
            source = "facebook";
          } else if (params.msclkid) {
            source = "microsoft_ads";
          }
        }

        const key = `${source}|${medium || ""}|${campaign || ""}`;
        const existing = sourceGroups.get(key) || {
          sessions: 0,
          conversions: 0,
          medium,
          campaign,
        };

        existing.sessions++;
        if (session.is_completed) existing.conversions++;
        sourceGroups.set(key, existing);
      }

      // Convert to array
      const results: MarketingSourceData[] = [];
      for (const [key, sourceData] of sourceGroups) {
        const [source] = key.split("|");
        results.push({
          source,
          medium: sourceData.medium,
          campaign: sourceData.campaign,
          sessions: sourceData.sessions,
          conversions: sourceData.conversions,
          conversion_rate:
            sourceData.sessions > 0
              ? Math.round(
                  (sourceData.conversions / sourceData.sessions) * 1000,
                ) / 10
              : 0,
        });
      }

      return results.sort((a, b) => b.sessions - a.sessions);
    } catch (error) {
      console.error("[SessionState] Error fetching marketing data:", error);
      return [];
    }
  },

  /**
   * Get journey paths for Sankey diagram
   */
  async getJourneyPaths(daysBack: number = 30): Promise<JourneyPath[]> {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - daysBack);

    try {
      const { data: entries, error } = await typedSupabase
        .from(FILTERED_ENTRIES)
        .select("session_id, step_name, created_at")
        .gte("created_at", dateFrom.toISOString())
        .order("session_id")
        .order("created_at");

      if (error || !entries) {
        console.error("[SessionState] Error fetching journey paths:", error);
        return [];
      }

      // Group by session and build paths
      const sessionPaths = new Map<string, AnalysisStep[]>();
      for (const entry of entries as Array<{
        session_id: string;
        step_name: string;
        created_at: string;
      }>) {
        const path = sessionPaths.get(entry.session_id) || [];
        // Only add if different from last step (avoid duplicates)
        if (path.length === 0 || path[path.length - 1] !== entry.step_name) {
          path.push(entry.step_name as AnalysisStep);
        }
        sessionPaths.set(entry.session_id, path);
      }

      // Count unique paths
      const pathCounts = new Map<string, number>();
      for (const [, path] of sessionPaths) {
        const key = path.join("→");
        pathCounts.set(key, (pathCounts.get(key) || 0) + 1);
      }

      // Convert to array and sort by count
      const results: JourneyPath[] = [];
      for (const [pathStr, count] of pathCounts) {
        results.push({
          path: pathStr.split("→") as AnalysisStep[],
          count,
        });
      }

      return results.sort((a, b) => b.count - a.count).slice(0, 50);
    } catch (error) {
      console.error("[SessionState] Error fetching journey paths:", error);
      return [];
    }
  },

  /**
   * Get total session and conversion counts
   */
  async getOverviewStats(daysBack: number = 30): Promise<{
    totalSessions: number;
    completedSessions: number;
    conversionRate: number;
  }> {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - daysBack);

    try {
      const { data, error, count } = await typedSupabase
        .from(FILTERED_SUMMARIES)
        .select("is_completed", { count: "exact" })
        .gte("started_at", dateFrom.toISOString());

      if (error || !data) {
        console.error("[SessionState] Error fetching overview stats:", error);
        return { totalSessions: 0, completedSessions: 0, conversionRate: 0 };
      }

      const typedData = data as Array<{ is_completed: boolean }>;
      const completed = typedData.filter((s) => s.is_completed).length;
      const total = (count as number | null) || typedData.length;

      return {
        totalSessions: total,
        completedSessions: completed,
        conversionRate:
          total > 0 ? Math.round((completed / total) * 1000) / 10 : 0,
      };
    } catch (error) {
      console.error("[SessionState] Error fetching overview stats:", error);
      return { totalSessions: 0, completedSessions: 0, conversionRate: 0 };
    }
  },

  // =========================================================================
  // DATE RANGE FILTERED METHODS
  // =========================================================================

  /**
   * Get overview stats with date range filter
   */
  async getOverviewStatsWithDateRange(
    dateFrom: Date | null,
    dateTo: Date | null,
    dateFilterType: DateFilterType,
    flow: AnalysisFlow | null,
  ): Promise<{
    totalSessions: number;
    completedSessions: number;
    conversionRate: number;
  }> {
    try {
      const dateColumn = getDateColumn(dateFilterType);
      const { data, error } = await typedSupabase.rpc(
        "get_analysis_overview_stats",
        {
          p_date_from: dateFrom
            ? dateFrom.toISOString()
            : "1970-01-01T00:00:00Z",
          p_date_to: dateTo
            ? new Date(
                new Date(dateTo).setDate(dateTo.getDate() + 1),
              ).toISOString()
            : new Date().toISOString(),
          p_date_col: dateColumn,
          p_flow: flow,
        },
      );

      const typedData = data as Array<{
        total_sessions: number;
        completed_sessions: number;
        conversion_rate: number;
      }> | null;

      if (error) throw error;
      // No rows is a legitimately empty range, unlike an error.
      if (!typedData || typedData.length === 0) {
        return { totalSessions: 0, completedSessions: 0, conversionRate: 0 };
      }

      return {
        totalSessions: Number(typedData[0].total_sessions),
        completedSessions: Number(typedData[0].completed_sessions),
        conversionRate: Number(typedData[0].conversion_rate),
      };
    } catch (error) {
      console.error("[SessionState] Error fetching overview stats:", error);
      throw error;
    }
  },

  /**
   * Get session summaries with date range filter
   */
  async getSessionSummariesWithDateRange(options: {
    limit?: number;
    offset?: number;
    maxStepFilter?: AnalysisStep | null;
    dateFrom?: Date | null;
    dateTo?: Date | null;
    dateFilterType: DateFilterType;
    completedOnly?: boolean;
    /** Marketing-source drill-down (from the source table). Matches the
     *  derived source: "direct" = no utm_source tag, otherwise utm_source.
     *  ponytail: the click-id-only ad sources (a gclid/fbclid with no
     *  utm_source that derive to google_ads/facebook) are not matched here —
     *  they'd need an RPC. The utm-tagged campaign rows (the common case) are
     *  exact. */
    sourceFilter?: string | null;
    flow?: AnalysisFlow | null;
  }): Promise<{ data: SessionSummary[]; count: number }> {
    try {
      const dateColumn = getDateColumn(options.dateFilterType);

      let query = typedSupabase
        .from(FILTERED_SUMMARIES)
        .select("*", { count: "exact" });

      if (options.maxStepFilter) {
        query = query.eq("max_step_reached", options.maxStepFilter);
      }

      if (options.dateFrom) {
        query = query.gte(dateColumn, options.dateFrom.toISOString());
      }

      if (options.dateTo) {
        const endDate = new Date(options.dateTo);
        endDate.setDate(endDate.getDate() + 1);
        query = query.lt(dateColumn, endDate.toISOString());
      }

      if (options.flow === "booking") {
        query = query.is("flow", null);
      } else if (options.flow) {
        query = query.eq("flow", options.flow);
      }

      if (options.completedOnly) {
        query = query.eq("is_completed", true);
      }

      if (options.sourceFilter) {
        query =
          options.sourceFilter === "direct"
            ? query.is("first_url_params->>utm_source", null)
            : query.eq("first_url_params->>utm_source", options.sourceFilter);
      }

      const offset = options.offset || 0;
      const limit = options.limit || 50;
      query = query
        .order(dateColumn, { ascending: false })
        .range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      // A null payload is a legitimately empty range, not a failure.
      return {
        data: (data as unknown as SessionSummary[]) || [],
        count: count || 0,
      };
    } catch (error) {
      console.error("[SessionState] Error fetching summaries:", error);
      throw error;
    }
  },

  /**
   * Get funnel data with date range filter
   */
  async getFunnelDataWithDateRange(
    dateFrom: Date | null,
    dateTo: Date | null,
    dateFilterType: DateFilterType,
    flow: AnalysisFlow | null,
  ): Promise<FunnelStep[]> {
    try {
      const dateColumn = getDateColumn(dateFilterType);
      const { data, error } = await typedSupabase.rpc("get_analysis_funnel", {
        p_date_from: dateFrom ? dateFrom.toISOString() : "1970-01-01T00:00:00Z",
        p_date_to: dateTo
          ? new Date(
              new Date(dateTo).setDate(dateTo.getDate() + 1),
            ).toISOString()
          : new Date().toISOString(),
        p_date_col: dateColumn,
        p_flow: flow,
      });

      if (error) throw error;
      // A null payload is a legitimately empty range, not a failure.
      return (data as unknown as FunnelStep[]) ?? [];
    } catch (error) {
      console.error("[SessionState] Error fetching funnel data:", error);
      throw error;
    }
  },

  /**
   * Get step transition times with date range filter
   */
  async getStepTransitionTimesWithDateRange(
    dateFrom: Date | null,
    dateTo: Date | null,
    flow: AnalysisFlow | null,
  ): Promise<StepTransitionTime[]> {
    try {
      const { data, error } = await typedSupabase.rpc(
        "get_analysis_step_transitions",
        {
          p_date_from: dateFrom
            ? dateFrom.toISOString()
            : "1970-01-01T00:00:00Z",
          p_date_to: dateTo
            ? new Date(
                new Date(dateTo).setDate(dateTo.getDate() + 1),
              ).toISOString()
            : new Date().toISOString(),
          p_flow: flow,
        },
      );

      if (error) throw error;
      // A null payload is a legitimately empty range, not a failure.
      return (data as unknown as StepTransitionTime[]) || [];
    } catch (error) {
      console.error(
        "[SessionState] Error calculating transition times:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get marketing source data with date range filter
   */
  async getMarketingSourceDataWithDateRange(
    dateFrom: Date | null,
    dateTo: Date | null,
    dateFilterType: DateFilterType,
    flow: AnalysisFlow | null,
  ): Promise<MarketingSourceData[]> {
    // Server-side aggregation (get_analysis_marketing_sources). This used to
    // pull EVERY session in the range to the browser in 1000-row pages and
    // group them in JS — tens of thousands of rows to render a dozen table
    // lines, and the one query that could half-fail, since a paging error
    // returned a truncated set that still looked like a valid answer.
    const dateColumn = getDateColumn(dateFilterType);
    const { data, error } = await typedSupabase.rpc(
      "get_analysis_marketing_sources",
      {
        p_date_from: dateFrom ? dateFrom.toISOString() : "1970-01-01T00:00:00Z",
        p_date_to: dateTo
          ? new Date(
              new Date(dateTo).setDate(dateTo.getDate() + 1),
            ).toISOString()
          : new Date().toISOString(),
        p_date_col: dateColumn,
        p_flow: flow,
      },
    );

    if (error) throw error;

    // A null payload is a legitimately empty range, not a failure.
    return (
      (data as unknown as Array<{
        source: string;
        medium: string | null;
        campaign: string | null;
        sessions: number;
        conversions: number;
        conversion_rate: number;
      }>) ?? []
    ).map((row) => ({
      source: row.source,
      medium: row.medium,
      campaign: row.campaign,
      sessions: Number(row.sessions),
      conversions: Number(row.conversions),
      conversion_rate: Number(row.conversion_rate),
    }));
  },

  /**
   * Get journey paths with date range filter
   */
  async getJourneyPathsWithDateRange(
    dateFrom: Date | null,
    dateTo: Date | null,
    flow: AnalysisFlow | null,
  ): Promise<JourneyPath[]> {
    try {
      const { data, error } = await typedSupabase.rpc(
        "get_analysis_journey_paths",
        {
          p_date_from: dateFrom
            ? dateFrom.toISOString()
            : "1970-01-01T00:00:00Z",
          p_date_to: dateTo
            ? new Date(
                new Date(dateTo).setDate(dateTo.getDate() + 1),
              ).toISOString()
            : new Date().toISOString(),
          p_flow: flow,
        },
      );

      if (error) throw error;
      // A null payload is a legitimately empty range, not a failure.
      return (
        (data as unknown as Array<{ path: string; count: number }>) || []
      ).map((item) => ({
        path: item.path.split("→") as AnalysisStep[],
        count: Number(item.count),
      }));
    } catch (error) {
      console.error("[SessionState] Error fetching journey paths:", error);
      throw error;
    }
  },

  /**
   * Search sessions by formData fields (email, phone, firstName, lastName)
   * Uses the search-sessions edge function with ILIKE. It answers only an
   * analysis user, so the call carries the signed-in session's token.
   */
  async searchSessionsByFormData(query: string): Promise<string[]> {
    try {
      const { data, error } = await supabase.functions.invoke(
        `search-sessions?q=${encodeURIComponent(query)}`,
        { method: "GET" },
      );

      if (error) {
        console.error("[SessionState] Search failed:", error);
        return [];
      }

      return data.session_ids || [];
    } catch (error) {
      console.error("[SessionState] Error searching sessions:", error);
      return [];
    }
  },

  /**
   * Get the last N events across all sessions (most recent first)
   * Respects active analysis filters (IP, user agent, non-production)
   */
  async getRecentEvents(
    limit: number,
    flow: AnalysisFlow | null,
  ): Promise<SessionStateEntry[]> {
    try {
      const { data, error } = await typedSupabase.rpc(
        "get_analysis_recent_events",
        { p_limit: limit, p_flow: flow },
      );

      if (error) throw error;
      // A null payload is a legitimately empty range, not a failure.
      return (data as unknown as SessionStateEntry[]) || [];
    } catch (error) {
      console.error("[SessionState] Error fetching recent events:", error);
      throw error;
    }
  },

  // =========================================================================
  // ANALYSIS FILTERS (IP / User Agent exclusions)
  // =========================================================================

  /**
   * Get all analysis filters
   */
  async getFilters(): Promise<AnalysisFilter[]> {
    try {
      const { data, error } = await typedSupabase
        .from("analysis_filters")
        .select("*")
        .order("filter_type")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("[SessionState] Error fetching filters:", error);
        return [];
      }

      return (data as unknown as AnalysisFilter[]) || [];
    } catch (error) {
      console.error("[SessionState] Error fetching filters:", error);
      return [];
    }
  },

  /**
   * Get active filters only
   */
  async getActiveFilters(): Promise<AnalysisFilter[]> {
    try {
      const { data, error } = await typedSupabase
        .from("analysis_filters")
        .select("*")
        .eq("is_active", true);

      if (error) {
        console.error("[SessionState] Error fetching active filters:", error);
        return [];
      }

      return (data as unknown as AnalysisFilter[]) || [];
    } catch (error) {
      console.error("[SessionState] Error fetching active filters:", error);
      return [];
    }
  },

  /**
   * Add a new filter
   */
  async addFilter(
    filterType: "ip" | "user_agent",
    pattern: string,
    description?: string,
  ): Promise<AnalysisFilter | null> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await typedSupabase
        .from("analysis_filters")
        .insert({
          filter_type: filterType,
          pattern,
          description: description || null,
          created_by: user?.email || null,
        })
        .select()
        .single();

      if (error) {
        console.error("[SessionState] Error adding filter:", error);
        return null;
      }

      return data as unknown as AnalysisFilter;
    } catch (error) {
      console.error("[SessionState] Error adding filter:", error);
      return null;
    }
  },

  /**
   * Update a filter (toggle active, update description)
   */
  async updateFilter(
    id: string,
    updates: Partial<
      Pick<AnalysisFilter, "is_active" | "description" | "pattern">
    >,
  ): Promise<boolean> {
    try {
      const { error } = await typedSupabase
        .from("analysis_filters")
        .update(updates)
        .eq("id", id);

      if (error) {
        console.error("[SessionState] Error updating filter:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("[SessionState] Error updating filter:", error);
      return false;
    }
  },

  /**
   * Delete a filter
   */
  async deleteFilter(id: string): Promise<boolean> {
    try {
      const { error } = await typedSupabase
        .from("analysis_filters")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("[SessionState] Error deleting filter:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("[SessionState] Error deleting filter:", error);
      return false;
    }
  },
};
